"use client";

import React, { useEffect, useState } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { ArrowRight, BrainCircuit, Cpu, Layers, Sparkles, Users as Users2, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface StepItem {
  id: number;
  vi: string;
  en: string;
}
interface SkillCategory {
  id: string;
  titleVi: string;
  titleEn: string;
  iconName: "tech" | "mind" | "soft";
  itemsVi: string;
  itemsEn: string;
}
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png-wvc-sizes",
  alt: "ROBOTO Mascot - Arms crossed"
};
const STEPS: StepItem[] = [{
  id: 1,
  vi: "Khám phá thử thách",
  en: "Discover the challenge"
}, {
  id: 2,
  vi: "Lên ý tưởng",
  en: "Ideate"
}, {
  id: 3,
  vi: "Thiết kế",
  en: "Design"
}, {
  id: 4,
  vi: "Lắp ráp",
  en: "Build"
}, {
  id: 5,
  vi: "Lập trình",
  en: "Program"
}, {
  id: 6,
  vi: "Thử nghiệm",
  en: "Test"
}, {
  id: 7,
  vi: "Cải tiến",
  en: "Improve"
}, {
  id: 8,
  vi: "Làm việc nhóm",
  en: "Teamwork"
}, {
  id: 9,
  vi: "Trình bày",
  en: "Present"
}, {
  id: 10,
  vi: "Thi đấu",
  en: "Compete"
}];
const SKILLS: SkillCategory[] = [{
  id: "technical",
  titleVi: "Kỹ thuật",
  titleEn: "Technical",
  iconName: "tech",
  itemsVi: "Lập trình, Thiết kế, Cơ khí, Điều khiển",
  itemsEn: "Programming, Design, Mechanics, Control"
}, {
  id: "mindset",
  titleVi: "Tư duy",
  titleEn: "Mindset",
  iconName: "mind",
  itemsVi: "Giải quyết vấn đề, Tư duy thuật toán, Tư duy hệ thống, Sáng tạo",
  itemsEn: "Problem solving, Algorithmic thinking, Systems thinking, Creativity"
}, {
  id: "softskills",
  titleVi: "Kỹ năng",
  titleEn: "Soft skills",
  iconName: "soft",
  itemsVi: "Làm việc nhóm, Giao tiếp, Thuyết trình, Quản lý thời gian",
  itemsEn: "Teamwork, Communication, Presentation, Time management"
}];
export default function RoboticsArena() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    const readInitialLang = () => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") {
          setLang(stored);
        }
      } catch {
        // Fallback default is 'vi'
      }
    };
    readInitialLang();
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: string;
      }>;
      if (customEvent.detail?.lang === "en" || customEvent.detail?.lang === "vi") {
        setLang(customEvent.detail.lang);
      } else {
        readInitialLang();
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const containerVariants: Variants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants: Variants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="roboticsarenajourney" data-nav="dark" className="relative overflow-hidden bg-foreground text-background py-20 md:py-28">
      {/* Decorative Technical Grid & Blueprint Motifs */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none">
          <defs>
            <pattern id="circuit-grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="currentColor" strokeWidth="1" />
              <circle cx="24" cy="24" r="1.5" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit-grid)" />
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Rail & Title Area */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex flex-col items-start gap-4 max-w-3xl">
            {/* Lime Green precision spec rail with square start notch */}
            <div className="flex items-center gap-2">
              <span className="size-2 bg-secondary inline-block shrink-0 shadow-xs" />
              <div className="h-px w-16 bg-secondary/80" />
              <Badge variant="outline" className="border-secondary/40 text-secondary bg-secondary/10 uppercase tracking-widest text-[11px] font-mono font-medium px-2.5 py-0.5 rounded-md">
                {lang === "vi" ? "Đấu trường công nghệ" : "Robotics Arena Track"}
              </Badge>
            </div>

            <div className="max-w-3xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-background">
                {lang === "vi" ? "ROBOTO trên đấu trường Robotics" : "ROBOTO in the robotics arena"}
              </h2>
              <p className="mt-4 text-base sm:text-lg text-background/80 leading-relaxed font-sans font-normal">
                {lang === "vi" ? "Khi nền tảng kiến thức vững vàng, học viên được định hướng bước tiếp từ các dự án lớp học sang sân chơi thi đấu robotics thực tế. Lộ trình rèn luyện chuyên sâu giúp các em làm quen với áp lực thời gian, chuẩn mực kỹ thuật và tinh thần kiên trì." : "Once classroom fundamentals are mastered, students move toward competitive robotics arenas. This pathway introduces realistic technical benchmarks, timed challenges, and collaborative discipline."}
              </p>
            </div>
          </div>

          {/* Static Floating Decorative Mascot */}
          <div className="flex items-center justify-start md:justify-end shrink-0 self-start md:self-start pointer-events-none select-none z-10">
            <motion.div animate={shouldReduceMotion ? undefined : {
            y: [0, -6, 0]
          }} transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut"
          }} className="relative flex items-center justify-center pointer-events-none select-none">
              <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt={DEFAULT_MASCOT.alt} className="w-20 h-20 sm:w-28 sm:h-28 lg:w-32 lg:h-32 object-contain bg-transparent pointer-events-none select-none drop-shadow-md" loading="lazy" decoding="async" />
            </motion.div>
          </div>
        </div>

        {/* 10-Step Connected Journey Timeline */}
        <div className="mt-14 sm:mt-16">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-background/70">
              <Layers className="size-4 text-secondary" />
              <span>
                {lang === "vi" ? "Quy trình thi đấu 10 bước chuẩn" : "10-Step Competition Pathway"}
              </span>
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 lg:gap-4 relative">
            {/* Connecting line motif behind the 10-step grid on desktop */}
            <div aria-hidden="true" className="hidden md:block absolute inset-0 pointer-events-none z-0">
              {/* Horizontal connector line behind row 1 (steps 01-05) */}
              <div className="absolute top-[38px] inset-x-6 h-px bg-secondary/30" />
              {/* Horizontal connector line behind row 2 (steps 06-10) */}
              <div className="absolute top-[calc(50%+38px)] inset-x-6 h-px bg-secondary/30" />
            </div>

            {STEPS.map((step, i) => <motion.div key={step.id} data-index={i} variants={itemVariants} className="group relative z-10 rounded-lg border border-background/15 bg-background/5 p-4 sm:p-5 flex flex-col justify-between transition-[background-color,border-color,box-shadow] duration-200 hover:border-secondary hover:bg-background/10 hover:shadow-md">
                {/* Circuit connector tag */}
                <div className="flex items-center justify-between w-full mb-3">
                  <div className="relative inline-flex items-center justify-center">
                    <span aria-hidden="true" className="absolute -inset-1 rounded-md bg-secondary/0 blur-xs transition-all duration-300 group-hover:bg-secondary/30 group-hover:animate-pulse pointer-events-none" />
                    <span className="relative font-mono text-2xl sm:text-3xl font-bold text-secondary transition-all duration-300 group-hover:text-secondary group-hover:animate-pulse">
                      {step.id < 10 ? `0${step.id}` : step.id}
                    </span>
                  </div>
                  <span className="size-2 rounded-full bg-background/30 group-hover:bg-secondary group-hover:animate-pulse transition-colors" />
                </div>

                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-background leading-snug uppercase tracking-tight">
                    {lang === "vi" ? step.vi : step.en}
                  </h3>
                </div>

                {/* Subtle bottom circuit edge */}
                <div className="mt-3 pt-2.5 border-t border-background/15 flex items-center justify-between text-[11px] font-mono text-background/50">
                  <span>STEP</span>
                  <span>{step.id}/10</span>
                </div>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Skills Matrix 3-Column Section */}
        <div className="mt-16 sm:mt-20">
          <div className="mb-6 flex items-center gap-2">
            <Sparkles className="size-4 text-accent" />
            <h3 className="text-xs sm:text-sm font-mono uppercase tracking-wider text-background/70">
              {lang === "vi" ? "Bộ năng lực thi đấu toàn diện" : "Comprehensive Competition Competencies"}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SKILLS.map((skill, i) => {
            const items = (lang === "vi" ? skill.itemsVi : skill.itemsEn).split(",").map(s => s.trim());
            return <div key={skill.id} data-index={i} className="rounded-xl border border-background/15 bg-background/5 p-6 backdrop-blur-xs flex flex-col justify-between transition-[background-color,border-color,box-shadow] duration-200 hover:border-secondary/70 hover:bg-background/10 hover:shadow-lg">
                  <div>
                    <div className="flex items-center justify-between pb-4 border-b border-background/15">
                      <div className="flex items-center gap-3">
                        <div className="size-9 rounded-md bg-secondary/20 text-secondary flex items-center justify-center shrink-0">
                          {skill.iconName === "tech" && <Cpu className="size-5" />}
                          {skill.iconName === "mind" && <BrainCircuit className="size-5" />}
                          {skill.iconName === "soft" && <Users2 className="size-5" />}
                        </div>
                        <h4 className="text-xl font-bold font-serif uppercase tracking-tight text-background">
                          {lang === "vi" ? skill.titleVi : skill.titleEn}
                        </h4>
                      </div>
                      <Wrench className="size-4 text-background/40" />
                    </div>

                    <ul className="mt-5 space-y-3">
                      {items.map((item, itemIdx) => <li key={item} data-index={itemIdx} className="flex items-center gap-3 text-sm text-background/90">
                          <span className="size-1.5 rounded-full bg-secondary shrink-0" />
                          <span className="font-sans font-medium">{item}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="mt-6 pt-4 border-t border-background/15 text-xs font-mono text-background/50 flex justify-between items-center tracking-wider">
                    <span>STATUS</span>
                    <span className="text-secondary font-semibold">ACTIVE</span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Dual Call-To-Action Cluster */}
        <div className="mt-16 sm:mt-20 p-8 rounded-2xl border border-background/15 bg-background/5 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="max-w-xl text-center sm:text-left">
            <h4 className="text-xl sm:text-2xl font-bold font-serif text-background">
              {lang === "vi" ? "Bắt đầu hành trình công nghệ cùng ROBOTO" : "Begin your technology journey with ROBOTO"}
            </h4>
            <p className="mt-2 text-sm text-background/70 font-sans">
              {lang === "vi" ? "Từng bước xây dựng nền móng tư duy kỹ thuật và bản lĩnh giải quyết vấn đề." : "Systematically cultivate technical problem-solving abilities and resilience."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3.5 w-full sm:w-auto">
            <Button asChild variant="outline" className="w-full sm:w-auto border-background/30 text-background hover:bg-background/20 hover:text-background h-11 px-5 rounded-lg font-sans text-sm font-semibold">
              <Link to="/LearningPathCurriculumPage">
                {lang === "vi" ? "Khám phá lộ trình học" : "Explore learning path"}
              </Link>
            </Button>

            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-lg font-sans text-sm font-semibold shadow-md">
              <Link to="/FreeTrialContactPage">
                <span>{lang === "vi" ? "Đăng ký tư vấn" : "Request consultation"}</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}